import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight, FaChevronDown } from "react-icons/fa";
import { getCurrentWeek, WeekSelector, dateToPeriodLabel, dateToLocalDateString } from '../reports/weeklyReports';
import { sendRequest, API_ROOT } from '../../supplementary_components/requests';
import { ListViewFooter } from '../../general_components/content_management/list_view/listViewFooter';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { StatementsPopup } from './statementsInPopup';
import { byDriverStatementGeneration } from './byDriverStatementGeneration';
import { generateShortId } from '../../supplementary_components/supplementaryMethods';
import { SelectableListView } from '../../general_components/content_management/reusableItems/selectableListView';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { addCurrency, roundCurrency } from './tables';
import styles from './statements.module.scss';
import { BiMessageDetail } from "react-icons/bi";

const MainWrapper = styled.div`
    padding-bottom:5rem;
    position: relative;
    &>.noData{
        text-align:center;
        padding-top:8rem;
        height:20rem;
        font-weight:700;
        font-size:1.6rem;
        color:var(--lighter-gray);
        &>button{
            margin: 2rem auto;
            display:block;
            background-color: var(--main-color);
            border:none;
            color:white;
            border-radius:0.4rem;
            font-size:1.2rem;
            font-weight:700;
            padding: 0rem 2rem;
            height:3.5rem;
            :hover{
                cursor:pointer;
            }
            :disabled{
                opacity:0.5;
                :hover{
                    cursor:default;
                }
            }
        }
    }
`
const ActionButtons = styled.div`
    display:flex;
    
    // height:1.6rem;
    // margin-bottom:1rem;
    &>button{
        // position: absolute; 
        // right: 0;
        border-radius:0.5rem;
        border:2px var(--main-color) solid;
        background-color:  var(--main-color);
        color:white;
        font-weight:600;
        padding: 0.4rem 0.6rem;
        cursor:pointer;
        &>svg{
            margin-bottom:-0.2rem;
            margin-right:-0.2rem;
        }
    }
    #actionButtonContent{
        &.invis{
            display:none;
        }
        z-index:2;
        position: absolute; 
        right: 0rem;
        margin-top:2.4rem;
        width:19rem;
        background-color:var(--bg);
        box-shadow:var(--on-hover-visible-object-shadow);
        border-radius:0.4rem;
        padding:0.8rem 0;
        &>div{
            margin: 0.4rem 0.8rem;
            font-weight:600;
        }
        &>.lbl{
            padding:0 0.4rem;
            color:var(--light-gray);
        }
        &>div:not(.lbl){
            -moz-user-select: none;
            -khtml-user-select: none;
            -webkit-user-select: none;
            border:none;
            background:none;
            color:var(--main-color);
            font-size:1.1rem;
            cursor:pointer;
            padding:0.2rem 0.4rem ;
        }
        
        &>hr{
            border: none; 
            height: 1px; 
            background-color: var(--lighter-gray);
            margin:1.1rem 0;
        }
    }
`

export function statusFormatter(val){
    switch (val) {
        case "inReview":
            return {title: "In Review", color: "var(--inProgressYellow)"};
        case "finalized":
            return {title: "Finalized", color: "var(--inProgressGreen)"};
        case "sent":
            return {title: "Sent", color: "var(--successGreen)"};
        case "paid":
            return  {title: "Paid", color: "var(--successGreen)"};    
        default:
            return "n/a"
    }
}

function StatementListConfGenerator (lbl, notePopupRef, setNotePopupData) {
    return {
        customRowStyling : [{padding:"1.25rem 0"}],
        idColumnName: "id",
        columns: [
            {
                lbl: "Driver Name",
                name: "name",
                sortable: true,
                customActionOnceClicked:true,
                customFlexGrow:"2",
                valueExtractor: (object) =>{
                    return `${object.driver.lastName}, ${object.driver.firstName} ${object.driver.middleName || ''}`;
                },
                conditionalStyling:()=>{
                    return [
                        // {fontWeight:"500"},
                        {flexGrow:"2"},
                        {cursor:"pointer"},
                        {textDecoration:"underline"},
                        {textDecorationColor:"var(--light-gray)"},
                    ]
                }
    
            },
            {
                lbl: "Truck Number(s)",
                name: "truckNumbers",
                customFlexGrow:"1",
                sortable: true,
                conditionalStyling:()=>{
                    return [{flexGrow:"1"}]
                },
                valueExtractor: (object) =>{
                    if(!object.truckNumbers){
                        return "-"
                    }
                    return object.truckNumbers.join(", ") || "-";
                },
            },
            {
                lbl: "DBA",
                name: "dba",
                customFlexGrow:"2",
                conditionalStyling:()=>{
                    return [{flexGrow:"2"}]
                },
                valueExtractor: (object) =>{
                    if(!object.driver.dbaInfo){
                        return "-"
                    }
                    return JSON.parse(object.driver.dbaInfo).dbaCompanyName || "-";
                },
            },
            {
                lbl: "Note",
                name: "statementNote",
                customFlexGrow:"0.5",
                conditionalStyling:()=>{
                    return [{flexGrow:"0.5"}]
                },
                // onClick: (object) =>{
                    
                // },
                valueExtractor: (object) =>{
                    if(!object.statementNote){
                        return ""
                    }
                    return <div className={styles.hasNoteIcon }><BiMessageDetail 
                    onClick={(e)=>{
                        const rect = e.currentTarget.getBoundingClientRect();
                        const popup = notePopupRef.current;
                        popup.style.position = 'fixed';
                        popup.style.left = `${rect.left-7}px`;
                        popup.style.top = `${rect.bottom + 3}px`;
                        popup.classList.add(styles.open);
                        setNotePopupData(object.statementNote);
                    }} 
                    /></div>;
                },
            },
            {
                lbl: "Type",
                name: "paymentType",
                conditionalStyling:()=>{
                    return [{color:"var(--little-light-gray)"}]
                },
                sortable: true,
                valueExtractor: (object) =>{
                    switch (object.paymentType) {
                        case "PerMile":
                            return `Per Mile ($${object.driver.perMileRate})`
                        case "Percentage":
                            return `Percentage (${object.driver.percentageFromGross * 100}%)`
                        case "Salary":
                            return `Salary ($${object.driver.salaryPerWeek})`
                        default:
                            return "-";
                    }
                },
            },
            {
                lbl: "Payout",
                name: "payout",
                customFlexGrow:"0.8",
                conditionalStyling:()=>{
                    return [{flexGrow:"0.8"}]
                },
                valueExtractor: (object) =>{
                    return object.status === "inReview" ? "-" : priceOnFinish(`${object.jobsGrandTotal}`)
                },
            },
            {
                lbl: "Bonuses" + (lbl["bonuses"] ? ` (${priceOnFinish(lbl["bonuses"])})` : ``),
                name: "totalBonuses",
                customFlexGrow:"0.8",
                conditionalStyling:(val)=>{
                    return [val !== "-" ? {color:"var(--preFinishGreen)"} : {}, {flexGrow:"0.8"}]
                },
                valueExtractor: (object) =>{
                    return object.status === "inReview" ? "-" : priceOnFinish(`${object.totalBonuses}`)
                },
            },
            {
                lbl: "Reimbursements" + (lbl["reimbursements"] ? ` (${priceOnFinish(lbl["reimbursements"])})` : ``),
                name: "totalReimbursements",
                customFlexGrow:"0.8",
                conditionalStyling:(val)=>{
                    // const style = 
                    return [val !== "-" ? {color:"var(--preFinishGreen)"} : {}, {flexGrow:"0.8"}]
                },
                valueExtractor: (object) =>{
                    return object.status === "inReview" ? "-" : priceOnFinish(`${object.totalReimbursements}`)
                },
            },
            {
                lbl: "Charges" + (lbl["charges"] ? ` (${priceOnFinish(lbl["charges"])})` : ``),
                name: "totalDeductions",
                customFlexGrow:"0.8",
                conditionalStyling:(val)=>{
                    return [val !== "-" ? {color:"var(--lightAlert)"} : {}, {flexGrow:"0.8"}]
                },
                valueExtractor: (object) =>{
                    return object.status === "inReview" ? "-" : priceOnFinish(`${object.totalDeductions}`)
                },
            },
            {
                lbl: "Net Pay",
                name: "netPay",
                customFlexGrow:"0.8",
                conditionalStyling:(val)=>{
                    return [val !== "-" ? {color:"var(--main-color)"} : {}, {fontWeight:"700"}, {flexGrow:"0.8"}]
                },
                valueExtractor: (object) =>{
                    const netPayout = object.status === "inReview" || (object.jobsGrandTotal + object.adjustmentsGrandTotal) <= 0 ? "-" : priceOnFinish(`${addCurrency(object.jobsGrandTotal, object.adjustmentsGrandTotal)}`, false, true)
                    return  netPayout; 
                },
            },
            {
                lbl: "Status",
                name: "amount",
                customFlexGrow:"0.5",
                valueExtractor : (object) =>{
                    return statusFormatter(object.status).title
                },
                conditionalStyling: (val)=>{
                    const result = [{flexGrow:"0.5"}];
                    switch (val) {
                        case "In Review":
                            result.push({color:"var(--inProgressYellow)"});
                            break;
                            // return [{color:"var(--inProgressYellow)"}];
                        case "Finalized":
                            result.push({color:"var(--inProgressGreen)"})
                            // return [{color:"var(--inProgressGreen)"}];
                            break;
                        case "Sent":
                            result.push({color:"var(--successGreen)"})
                            // return [{color:"var(--successGreen)"}];
                            break;
                        case "Paid":
                            result.push({color:"var(--successGreen)"})
                            // return [{color:"var(--successGreen)"}];
                            break;
                        default:
                            // return [];
                    }
                    return   result;
                }
            }
        ]
    }
} 
export const Statements = () => {
    const [selectedPeriod, setSelectedPeriod] = useState(getCurrentWeek(localStorage.getItem("currentFirstDayOfAWeek"), true));
    const [statementsList, setStatementsList] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const listRef = useRef(null);
    const actionsButton = useRef(null);
    const selectedLabel = useRef(null);
    const selectedHr = useRef(null);
    const sendButton = useRef(null);
    const notePopup = useRef(null);
    const actionButtonContent = useRef(null);
    const markAsPaidButton = useRef(null);
    const totalDriverPayout = useRef(null);
    const totalReimbursements = useRef(null);
    const totalDeductions = useRef(null);
    const totalBonuses = useRef(null);
    const [pagonationData, setPagonationData] = useState({"currentPageNumber":0});
    const [updateAvailable, setUpdateAvailable] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [updatingTheList, setUpdatingTheList] = useState(false);
    const [selectedStatements, setSelectedStatements] = useState([]);
    const [error, setError] = useState(null);
    const [openStatementsByDrivers, setOpenStatementsByDrivers] = useState(null);
    const [currentPeriodId, setCurrentPeriodId] = useState(null);
    const [notePopupData, setNotePopupData] = useState(null);

    useEffect(() => {
        if (notePopupData == null) return;
        const handleDocumentClick = (e) => {
            if (notePopup.current && !notePopup.current.contains(e.target)) {
                notePopup.current.classList.remove(styles.open);
                setNotePopupData(null);
            }
        };
        const id = setTimeout(() => document.addEventListener('click', handleDocumentClick), 0);
        return () => {
            clearTimeout(id);
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [notePopupData]);

    const _seData = (data) => {
        setGenerating(false);
        setUpdateAvailable(false);
        setStatementsList(data.dataset || data);
        delete data["dataset"]; 
        setCompanyInfo(data.companyData || null);
        delete data["companyData"]; 
        setCurrentPeriodId(data.currentPeriodId || null);
        delete data["currentPeriodId"]; 
        totalDriverPayout.current = data.driverPayoutTotal;
        totalReimbursements.current = data.totalReimbursements;
        totalDeductions.current = data.totalDeductions;
        totalBonuses.current = data.totalBonuses;
        delete data["driverPayoutTotal"]; 
        delete data["totalReimbursements"]; 
        delete data["totalDeductions"]; 
        delete data["totalBonuses"]; 
        setPagonationData(data);
    }
    function removeStatmentFromCurrentTotals (statement){
        totalDriverPayout.current -= addCurrency(statement.jobsGrandTotal, statement.adjustmentsGrandTotal);
        totalReimbursements.current -= statement.totalReimbursements;
        totalDeductions.current -= statement.totalDeductions;
        totalBonuses.current -= statement.totalBonuses;
    }
    function _setSelectedStatements(newSelectedStatements){
        if(newSelectedStatements.length === 0){
            selectedLabel.current.style.display = "none";
            sendButton.current.style.display = "none";
            markAsPaidButton.current.style.display = "none";
            selectedHr.current.style.display = "none";
        }else if(newSelectedStatements[0] === "ignoreFollowing" && newSelectedStatements.length === statementsList.length+1){
            selectedLabel.current.style.display = "none";
            sendButton.current.style.display = "none";
            markAsPaidButton.current.style.display = "none";
            selectedHr.current.style.display = "none";
        }else{
            let sendBtnDisabled = false;
            let markBtnDisabled  = false;
            for(let statement of statementsList){
                if((newSelectedStatements[0] === "ignoreFollowing" && newSelectedStatements.includes(statement.id))
                ||(newSelectedStatements[0] !== "ignoreFollowing" && newSelectedStatements[0] !== "selectAll" && !newSelectedStatements.includes(statement.id))){
                    continue;
                }
                if(statement.status === "inReview"){
                    sendBtnDisabled = true;
                    markBtnDisabled = true;
                    break;
                }else if(statement.status === "finalized"){
                    markBtnDisabled = true;
                }else if(statement.status === "sent"){
                    sendBtnDisabled = true;
                }else if(statement.status === "paid"){
                    sendBtnDisabled = true;
                    markBtnDisabled = true;
                }
            }
            if(sendBtnDisabled && markBtnDisabled){
                selectedLabel.current.style.display = "none";
                selectedHr.current.style.display = "none";
            }else{
                selectedLabel.current.style.display = "block";
                selectedHr.current.style.display = "block";
            }
            sendButton.current.style.display = sendBtnDisabled ? "none" : "block";
            markAsPaidButton.current.style.display = markBtnDisabled ? "none" : "block";
        }
        // 
        setSelectedStatements(newSelectedStatements);
    }
    
    const handleClickOutside = (event) => {
        if (actionsButton.current && !actionsButton.current.contains(event.target) &&  actionButtonContent.current && !actionButtonContent.current.contains(event.target)){
            closeSection(actionButtonContent.current);
        }
    };
    useEffect(
        () => {
            if(updateAvailable){                
                setError(null);
                setStatementsList(null);
                const controller = new AbortController();
                const signal = controller.signal;
                sendRequest('GET', API_ROOT+`statements/byWeek?pageNumber=${pagonationData.currentPageNumber? pagonationData.currentPageNumber : 0}&weekStartDate=${dateToLocalDateString(selectedPeriod)}`, signal, _seData, setError, null);
                return () => {
                    // cancel the request before component unmounts
                    controller.abort();
                };
            }
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        // eslint-disable-next-line
        }, [updateAvailable]
    );
    if (statementsList==null && error ==null)return<LoadingIcon state={true} height={'10rem'} />;
    const changePeriod = (next) => {
        setSelectedPeriod((prev)=>{
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + (next ? 7 : (-7)));
            return newDate;
        });
        setUpdateAvailable(true);
    }
    function closeSection(){
        if(actionButtonContent.current && !actionButtonContent.current.className.includes("invis")){
            actionButtonContent.current.className += " invis";
        }
    }
    function sendActionRequest(statementsArray, action, jobId, newStatus){
        if(action === "checkingJobStatus"){
            setTimeout(() => {
                sendRequest("GET", API_ROOT+"statements/jobStatus?jobId="+jobId, null, (res)=>{
                    if(Object.keys(res).length < 1){
                        sendActionRequest(null, "checkingJobStatus", jobId, newStatus);
                    }else {
                        setUpdatingTheList(false);
                        if(res["customMessage"] === "reloadPage"){
                            window.location.reload();
                            // setUpdateAvailable(true);
                        }else{
                            setStatementsList(prev =>
                                prev.map(statement =>
                                    res[jobId][statement.id] === true
                                    ? { ...statement, status: newStatus}
                                    : statement
                                )
                            );
                            
                        }
                    }
                }, (e)=>{console.log(e)})                
            }, 1000);
        }else{
            closeSection();
            setUpdatingTheList(true);
            if(action === "markAsSent"){
                const jobId = generateShortId();
                sendRequest("POST", API_ROOT+"statements/send?companyId="+companyInfo.id+"&periodId="+currentPeriodId+"&jobId="+jobId, null, (res)=>{
                    if(res){
                        sendActionRequest(null, "checkingJobStatus", jobId, "sent");
                    }
                }, ()=>{}, statementsArray);
            }else if(action === "markAsPaid"){
                sendRequest("POST",  API_ROOT+"statements/markAsPaid?companyId="+companyInfo.id+"&periodId="+currentPeriodId, null, (res)=>{
                    setUpdatingTheList(false);
                    setStatementsList(prev =>
                        prev.map(statement =>
                            res[statement.id] === true
                            ? { ...statement, status: newStatus}
                            : statement
                        )
                    );
                }, ()=>{}, statementsArray);
            }
        }
    }
    if (statementsList==null && error ==null);
    if (error !== null)return <div className='errorMessage'>{error.customError?error.customError:"Something went wrong"}</div>
    function updateStatementNote(statementId, note){
        setStatementsList(prev => {
            return prev.map(statement => {
                if(statement.id === statementId){
                    return {...statement, statementNote: note};
                }
                return statement;
            });
        });
    }
    function changeStatementStatus(statementId, newStatus, updatedAdjustmentsGrandTotal, updatedTotalBonuses, updatedTotalDeductions, updatedTotalReimbursements, updatedJobsGrandTotal){
        if(newStatus === "remove"){
            setStatementsList(prevList => {
                    return prevList.filter(statement => {
                        if(statement.id !== statementId)return true;
                        if(statement.status !== "inReview"){
                            removeStatmentFromCurrentTotals(statement);
                        }
                        return false;
                    })
                }
            );
            return;
        }
        setStatementsList(prev =>
            prev.map(statement => {
                if(statement.id === statementId){
                    const statementCopy = {...statement};
                    try {
                        if(statementCopy.status !== "inReview" && newStatus === "inReview"){
                            removeStatmentFromCurrentTotals(statementCopy);
                        }else if (newStatus !== "inReview" && updatedAdjustmentsGrandTotal != null){
                            const jobsTotal = updatedJobsGrandTotal != null ? updatedJobsGrandTotal : statementCopy.jobsGrandTotal;
                            if(statementCopy.status === "inReview"){
                                totalDriverPayout.current += addCurrency(jobsTotal, updatedAdjustmentsGrandTotal);
                                totalReimbursements.current += updatedTotalReimbursements;
                                totalDeductions.current += updatedTotalDeductions;
                                totalBonuses.current += updatedTotalBonuses;
                            }else{
                                totalDriverPayout.current += addCurrency(updatedJobsGrandTotal ?? statementCopy.jobsGrandTotal, updatedAdjustmentsGrandTotal) - addCurrency(statementCopy.jobsGrandTotal, statementCopy.adjustmentsGrandTotal);
                                totalReimbursements.current += updatedTotalReimbursements - statementCopy.totalReimbursements;
                                totalDeductions.current += updatedTotalDeductions - statementCopy.totalDeductions;
                                totalBonuses.current += updatedTotalBonuses - statementCopy.totalBonuses;
                            }
                        }
                    } catch (error) {
                    }
                    statementCopy.status = newStatus;
                    if(updatedAdjustmentsGrandTotal != null){
                        statementCopy.adjustmentsGrandTotal = updatedAdjustmentsGrandTotal;
                        statementCopy.totalReimbursements = updatedTotalReimbursements;
                        statementCopy.totalDeductions = updatedTotalDeductions;
                        statementCopy.totalBonuses = updatedTotalBonuses;
                    }
                    if(updatedJobsGrandTotal != null){
                        statementCopy.jobsGrandTotal = updatedJobsGrandTotal;
                    }
                    return statementCopy;
                }else{
                    return statement
                }
            }
            )
        );
    }
    return <MainWrapper ref={listRef}>
        <LoadingIcon state={updatingTheList} height={listRef.current ? listRef.current.offsetHeight+"px" : "10rem"} />
        <WeekSelector>
            <FaChevronLeft onClick={()=>{changePeriod(false)}} />
            <span className="non-selectable">{dateToPeriodLabel(selectedPeriod)}</span>
            <FaChevronRight onClick={()=>{changePeriod(true)}} />
        </WeekSelector>
        {statementsList && statementsList.customResponse ? 
             <div className="noData non-selectable">
                 <LoadingIcon id="loadingIcon" state={generating} height={'10rem'} />
                {statementsList.customResponse}
                <br />
                {statementsList.generateButton ?
                <button onClick={(e)=>{
                    e.preventDefault();
                    e.target.disabled = true;
                    e.target.innerText = 'Generating';
                    setGenerating(true);

                    // e.target
                    sendRequest('POST', API_ROOT+`statements/generate?weekStartDate=${dateToLocalDateString(selectedPeriod)}`, null, _seData, setError, null);
                }}>
                    Generate Statements
                </button>
                
                :null}
                
            </div>
        :<>
        {
            statementsList ? 
            <>
            <div className={styles.controlPanel}>
                {/* <div className={styles.driverTotalPayout}>
                    Total Payout: <span>{priceOnFinish(totalDriverPayout.current)}</span>
                </div> */}
                <ActionButtons>
                    {/* <button  disabled >Send</button>
                    <button  disabled>Mark As Paid</button> */}
                    
                    <button 
                    ref={actionsButton}
                    onClick={()=>{
                        let className = actionButtonContent.current.className;
                        if(className.includes("invis")){
                            actionButtonContent.current.className = className.replace("invis", "");
                        }else{
                            closeSection();
                        }
                    }} 
                    >Actions <FaChevronDown /></button>
                    <div ref={actionButtonContent} className="invis" id="actionButtonContent">
                        <div className="lbl" style={{display:"none"}} ref={selectedLabel}>Selected</div>
                        <div ref={sendButton} style={{display:"none"}} onClick={()=>{sendActionRequest(selectedStatements, "markAsSent")}}>Send</div>
                        <div ref={markAsPaidButton} style={{display:"none"}} onClick={()=>{sendActionRequest(selectedStatements, "markAsPaid", null, 'paid')}}>Mark As Paid</div>
                        <hr ref={selectedHr} style={{display:"none"}} />
                        <div className="lbl">All Statements</div>
                        <div  onClick={()=>{sendActionRequest(["allFinalized"], "markAsSent")}}>Send All Finalized</div>
                        <div  onClick={()=>{sendActionRequest(["allSent"], "markAsPaid", null, 'paid')}}>Mark All Sent As Paid</div>
                        <hr />
                        <div className="lbl" >Generate</div>
                        <div onClick={
                            ()=>{
                                closeSection(actionButtonContent.current);
                                byDriverStatementGeneration(dateToLocalDateString(selectedPeriod), setOpenStatementsByDrivers, setStatementsList)
                            }
                        }>Generate Statement By Driver</div>
                    </div>
                </ActionButtons>
            </div>
            <SelectableListView tblConf={StatementListConfGenerator({"payout" : totalDriverPayout.current}, notePopup, setNotePopupData)} data={statementsList} listRef={listRef} selectedList={selectedStatements} setSelectedList={_setSelectedStatements} customAction={(statement)=>{
                let driverIds = [statement.driver.id];
                if(statement.coDriversIds && statement.coDriversIds.length > 0){
                    driverIds = driverIds.concat(statement.coDriversIds);
                }
                setOpenStatementsByDrivers({driverIds:driverIds, periodId:statement.period.id})
            }} />

            <div className={styles.stickyLowerContainer}>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div><span className={styles.lbl}>Totals: </span></div>
                        <div><span className={styles.totalBonuses}>{priceOnFinish(roundCurrency(totalBonuses.current))}</span></div>
                        <div><span className={styles.totalBonuses}>{priceOnFinish(roundCurrency(totalReimbursements.current))}</span></div>
                        <div><span className={styles.totalCharges}>{priceOnFinish(roundCurrency(totalDeductions.current))}</span></div>
                        <div><span className={styles.totalPayout}>{priceOnFinish(roundCurrency(totalDriverPayout.current))}</span></div>
                        <div></div>
                    </div>
            </>
            :
            <div className="noData">Somethig went wrong</div>
        }
        </>}
        
        <ListViewFooter paginationData={{
                data: pagonationData,
                setPaginationData: setPagonationData
            }} resultCount={statementsList&&statementsList.length?statementsList.length:0} requestReload={()=>{setUpdateAvailable(true);}} />
        {openStatementsByDrivers?
            <StatementsPopup data={openStatementsByDrivers} companyData={companyInfo} setOpenStatementsByDrivers={setOpenStatementsByDrivers} changeStatementStatus={changeStatementStatus}  updateStatementNote={updateStatementNote}/>
            :null}
        <div ref={notePopup} className={styles.notePopup}><div></div><div>{notePopupData||""}</div></div>
    </MainWrapper>
}
